/** @jsx m */
import { initMithrilComponent } from '@client-shared/utils/mithril'
import m from 'mithril'
import Portal from 'mithril-portal'
import css from './toast.module.css'
import { v4 as uuidv4 } from 'uuid'

// -----------------------------
// types
// -----------------------------
type NotificationType = 'info' | 'warning' | 'success' | 'danger'

interface NotificationItem {
	id: string
	type: NotificationType
	text: m.Children
	timeout_ms: number
}

interface NotificationAttrs extends NotificationItem {}

interface NotificationsStateShape {
	list: NotificationItem[]
	destroy: (msg: NotificationItem) => void
}

// -----------------------------
// state
// -----------------------------
const notification_state: NotificationsStateShape = {
	list: [],
	destroy(msg) {
		const index = notification_state.list.findIndex((item) => item.id === msg.id)
		if (index !== -1) notification_state.list.splice(index, 1)
	},
}

// -----------------------------
// functions (kept as-is)
// -----------------------------
function internal_addListItem(type: NotificationType, text: m.Children, timeout_ms = 3000) {
	// prevent duplicate notifications from being added
	if (notification_state.list.findIndex((item) => item.type === type && item.text === text) > -1) {
		return
	}

	notification_state.list.push({ id: uuidv4(), type, text, timeout_ms })
	m.redraw()
}

function addInfo(text: m.Children, timeout_ms: number = 3000): void {
	internal_addListItem('info', text, timeout_ms)
}

function addSuccess(text: m.Children, timeout_ms: number = 3000): void {
	internal_addListItem('success', text, timeout_ms)
}

function addWarning(text: m.Children, timeout_ms: number = 3000): void {
	internal_addListItem('warning', text, timeout_ms)
}

function addDanger(text: m.Children, timeout_ms: number = 3000): void {
	internal_addListItem('danger', text, timeout_ms)
}

function dismissAll(): void {
	if (!notification_state.list.length) return
	notification_state.list.splice(0, notification_state.list.length)
	m.redraw()
}

// -----------------------------
// exported object API
// -----------------------------
export const notifications = {
	addInfo,
	addSuccess,
	addWarning,
	addDanger,
	dismissAll,
}

// -----------------------------
// components
// -----------------------------
const Notification = initMithrilComponent<NotificationAttrs>()

Notification.oninit = (vnode) => {
	const timeout_ms = vnode.attrs.timeout_ms
	setTimeout(() => {
		if (vnode.dom) (vnode.dom as HTMLElement).classList.add('destroy')
		setTimeout(() => {
			notification_state.destroy(vnode.attrs)
			m.redraw()
		}, 300)
	}, timeout_ms)
}

Notification.view = (vnode) => {
	const msg = vnode.attrs
	const valid_types: { type: NotificationType; class: string }[] = [
		{ type: 'info', class: css.info },
		{ type: 'warning', class: css.warning },
		{ type: 'success', class: css.success },
		{ type: 'danger', class: css.danger },
	]
	const type_class = valid_types.find((item) => item.type === msg.type)?.class || css.info

	return (
		<div className={css.notification_container}>
			<div
				className={[css.m_notification, type_class].join(' ')}
				onclick={() => {
					if (vnode.dom) (vnode.dom as HTMLElement).classList.add('destroy')
					setTimeout(() => {
						notification_state.destroy(msg)
						m.redraw()
					}, 300)
				}}
			>
				{msg.text}
			</div>
		</div>
	)
}

const Notifications = initMithrilComponent()

Notifications.view = () => {
	if (!notification_state.list.length) return null
	return (
		<Portal>
			<div className={css.m_notifications}>
				{notification_state.list.map((msg) => (
					<div key={msg.id}>
						<Notification {...msg} />
					</div>
				))}
			</div>
		</Portal>
	)
}

export default Notifications
